import {
  Box,
  Drawer,
  HStack,
  Link,
  Portal,
  Stack,
  Text,
  useDisclosure
} from "@chakra-ui/react"
import { CheckIcon, ChevronRightIcon, XIcon } from "@pathwright/pathicons"
import IconButton from "@pathwright/ux/src/components/button/IconButton"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useRef } from "react"
import RoleBadge from "./RoleBadge"
import { useRolesConfigWithLabels } from "./rolesConfig"

const tPrefix = "share_card.link_tab.roles"

// TODO: move to some utils file
const runIfFn = (possibleFn, ...args) => {
  if (typeof possibleFn === "function") {
    return possibleFn(...args)
  }
  return possibleFn
}

const RoleHeader = ({ role }) => (
  <HStack>
    <RoleBadge label={role.label} color={role.color} highlight />
    <Text as="span" fontStyle="italic" color="gray.500">
      {role.includes}
    </Text>
  </HStack>
)

const RoleFeature = ({ role, feature }) => (
  <HStack key={feature.label}>
    <Box>
      <CheckIcon color={role.color} size="1em" />
    </Box>
    <Box css={{ "& * + *": { marginLeft: ".4em" } }}>
      {!!feature.label && (
        <Text as="span" fontWeight="bold">
          {feature.label}:
        </Text>
      )}
      <Text as="span">{feature.description}</Text>
    </Box>
  </HStack>
)

const LearnMoreLink = () => {
  const { tc } = useTranslate()

  return (
    <HStack gap={1}>
      <Link
        href="https://help.pathwright.com/en/articles/2083448-pathwright-member-roles"
        target="_blank"
        variant="underline"
      >
        {tc(`${tPrefix}.Learn more about roles`)}
      </Link>
      <ChevronRightIcon size="1em" />
    </HStack>
  )
}

const RoleDescriptorsDrawer = ({ children }) => {
  const { open, onOpen, onClose } = useDisclosure()
  const btnRef = useRef()
  const rolesConfig = useRolesConfigWithLabels()

  return (
    <>
      {runIfFn(children, { ref: btnRef, onOpen })}
      <Drawer.Root
        open={open}
        positioning={{ placement: "right" }}
        onOpenChange={(e) => !e.open && onClose()}
        finalFocusEl={() => btnRef.current}
        size="md"
      >
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content className="RoleDescriptorsDrawer">
              <Drawer.CloseTrigger asChild>
                <IconButton
                  variant="subtle"
                  aria-label="Close"
                  icon={<XIcon />}
                  roundSize={8}
                />
              </Drawer.CloseTrigger>
              <Drawer.Header /> {/* for space */}
              <Drawer.Body>
                <Stack gap={4}>
                  {Object.entries(rolesConfig)
                    .filter(([key, role]) => role.features?.length)
                    .map(([key, role]) => (
                      <Stack key={key}>
                        <RoleHeader role={role} />
                        {role.features.map((feature) => (
                          <RoleFeature
                            key={feature.label}
                            role={role}
                            feature={feature}
                          />
                        ))}
                      </Stack>
                    ))}
                </Stack>
              </Drawer.Body>
              <Drawer.Footer justifyContent="flex-start">
                <LearnMoreLink />
              </Drawer.Footer>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </>
  )
}

export default RoleDescriptorsDrawer
